<template>
	<CDialog
		:visible="show"
		:title="title"
		:max-width="dialogMaxWidth"
		@close="$emit('close')"
	>
		<template v-if="isPersonField && personSubmissionsEnabled">
			<div class="flex gap-4 items-center">
				<Inline class="b5:show b5:w-33" svg="complex-icons/person-field" />
				<div class="b5:w-66">
					<p class="mb-4">Person Fields let you select people — like employees, clients, or volunteers — from entries in a <a href="/support/472/building-forms/creating-data-lookups/people-lookups" target="_blank">Person Form.<Inline svg="support" class="align-bottom mx-2" /></a> You can then pull their names, emails, photos, and more into any of your forms.</p>
					<p class="mb-4">To get started, you'll need to create a Person Form.</p>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="flex gap-4 items-start">
				<div class="b5:w-66">
					<template v-if="isPersonField">
						<p class="mb-4">
							The Person field allows you to select from a list of people - like employees, clients, volunteers, or anyone involved in your workflow processes. This list is based on entries from another form, where each entry represents a person.
						</p>
						<p class="mb-4">
							Using a Person field, you can map this information (including names, emails, photos, and more) on any form and then use that data in calculations, conditional logic scenarios, and form content.
						</p>
						<ul class="list-style mb-4">
							<li><strong>Step 1:</strong> Set up a form where each entry represents a person. Populate the entries with Name, Email, Photo, and any other information you want to include.</li>

							<li><strong>Step 2:</strong> Enable "Use With Person Field?" in the form settings and map the relevant fields (Name, Email, Photo, and Active Status).</li>

							<li><strong>Step 3:</strong> Return to this form and add a Person field. Select the form you created in the "Look Up People From" dropdown. Choose the fields you want to display as your Choice Labels.</li>
						</ul>

						<p><a href="https://www.cognitoforms.com/support/472/building-forms/creating-data-lookups/people-lookups" target="_blank">Check out our tutorials</a> for detailed instructions and support.</p>
					</template>
					<template v-else>
						<p class="mb-4">The Lookup field is a powerful tool that allows you to reference entries from other forms in your Choice fields. This means you can create dynamic forms that pull in data from different sources, making your forms more interactive and efficient.</p>
						<p class="mb-4">The Lookup field is basically a Choice field where the available choices are based on entries from other forms. You can then use that entry data for calculations, conditional logic scenarios, and in form content.</p>
						<p class="mb-4">You'll need multiple forms to use this feature. Here's a quick way to start:</p>
						<ul class="list-style mb-4">
							<li><strong>Step 1:</strong> Set up your source form. This is the form whose entry data you want to reference in this form.</li>

							<li><strong>Step 2:</strong> Come back to this form and select that source form in the "Look Up Choices From" dropdown.</li>

							<li><strong>Step 3:</strong> Choose the fields you want to display as your Choice Labels and finish configuring your lookup field.</li>
						</ul>

						<p><a href="https://www.cognitoforms.com/support/201/building-forms/creating-data-lookups" target="_blank">Check out our tutorials</a> for detailed instructions and support.</p>
					</template>
				</div>

				<Inline class="b5:show b5:w-33" :svg="`complex-icons/${ isPersonField ? 'person-field' : 'lookup-field'}`" />
			</div>
		</template>
		<template v-if="isPersonField && personSubmissionsEnabled" #footer>
			<CButton
				id="OkayButton"
				class="button--primary"
				@click="onCreatePersonForm"
			>
				Create Person Form
			</CButton>
		</template>
		<template v-else #footer>
			<CButton
				id="OkayButton"
				class="button--primary"
				@click="$emit('close', $event)"
			>
				Okay
			</CButton>
		</template>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import session from '@cognitoforms/api/services/session';

	export default defineComponent({
		name: 'CLookupFieldWelcomeDialog',
		components: {
			CDialog,
			CButton
		},
		props: {
			show: {
				type: Boolean,
				default: false
			},
			isPersonField: {
				type: Boolean,
				default: false
			}
		},
		emits: ['close', 'create'],
		computed: {
			personSubmissionsEnabled(): boolean {
				return session.value?.enabledFeatures?.PersonSubmissions ?? false;
			},
			dialogMaxWidth(): string {
				return this.isPersonField && this.personSubmissionsEnabled ? '555px' : '800px';
			},
			title() {
				if (this.isPersonField && this.personSubmissionsEnabled)
					return 'Meet the Person Field';

				return this.isPersonField ? 'Welcome to using the Person Field' : 'Welcome to using the Lookup Field';
			}
		},
		methods: {
			onCreatePersonForm(): void {
				this.$emit('create');
				this.$emit('close');
			}
		}
	});
</script>
